import { useEffect } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { config } from '../../lib/config';
import { toast } from '../../shared/ui/toast';
import { addNotification, type AppNotification } from './store';

interface IncomingNotification {
  id: string;
  type: string;
  accountId?: string | null;
  mailboxId: string;
  messageId: string;
  from: string;
  subject?: string | null;
  attachmentCount: number;
  createdAt: string;
  minutes?: number | null;
  percent?: number | null;
  usedMb?: number | null;
  limitMb?: number | null;
}

/** Lifecycle-типы (promt-78 §C): SSE `event:` = type. Приходят помимо message.received. */
const LIFECYCLE_EVENTS = [
  'mailbox.expiring',
  'mailbox.size.threshold',
  'subscription.quota.warning',
  'subscription.quota.reached',
] as const;

/**
 * Подписка на SSE-поток уведомлений bff-ui (`GET /api/notifications/stream`).
 * При событии `message.received`: кладёт уведомление в стор, показывает toast и
 * инвалидирует кэш писем/ящиков, чтобы списки обновились без перезагрузки.
 * EventSource сам переподключается (сервер шлёт `retry:`); при ошибке деградируем тихо.
 *
 * `activeSubscriptionId` — только ключ переподключения: при смене активной подписки
 * сервер заново вычисляет ключи аккаунтов (cookie `k1.bff.active-sub`), поэтому поток
 * пересоздаём, чтобы гарантированно охватить новый аккаунт.
 */
export function useNotificationStream(enabled: boolean, activeSubscriptionId?: string | null): void {
  const queryClient = useQueryClient();
  const { t } = useTranslation();

  useEffect(() => {
    if (!enabled) return;

    const url = `${config.apiBase}/api/notifications/stream`;
    const source = new EventSource(url, { withCredentials: true });

    const onMessageReceived = (event: MessageEvent<string>) => {
      let data: IncomingNotification;
      try {
        data = JSON.parse(event.data) as IncomingNotification;
      } catch {
        return;
      }

      const notification: Omit<AppNotification, 'read'> = {
        id: data.id,
        type: data.type,
        accountId: data.accountId,
        mailboxId: data.mailboxId,
        messageId: data.messageId,
        from: data.from,
        subject: data.subject,
        attachmentCount: data.attachmentCount,
        createdAt: data.createdAt,
      };
      addNotification(notification);

      toast.info(t('notifications.newMessage'), data.subject ?? data.from);

      void queryClient.invalidateQueries({ queryKey: ['mailbox', data.mailboxId, 'messages'] });
      void queryClient.invalidateQueries({ queryKey: ['mailbox', data.mailboxId] });
      void queryClient.invalidateQueries({ queryKey: ['mailboxes'] });
      // Входящее письмо увеличивает израсходованный трафик — обновляем остаток
      // лимита без перезагрузки страницы (promt-79 §1).
      void queryClient.invalidateQueries({ queryKey: ['quota'] });
    };

    // Lifecycle-уведомления: кладём в стор, тостим по type, обновляем квоту/ящики
    // (лимит/деактивация могли изменить состояние без message.received).
    const onLifecycle = (event: MessageEvent<string>) => {
      let data: IncomingNotification;
      try {
        data = JSON.parse(event.data) as IncomingNotification;
      } catch {
        return;
      }

      addNotification({
        id: data.id,
        type: data.type,
        accountId: data.accountId,
        mailboxId: data.mailboxId,
        messageId: data.messageId,
        from: data.from,
        subject: data.subject,
        attachmentCount: data.attachmentCount,
        createdAt: data.createdAt,
        minutes: data.minutes,
        percent: data.percent,
        usedMb: data.usedMb,
        limitMb: data.limitMb,
      });

      toast.info(t(`notifications.lifecycle.${data.type}.title`), data.subject ?? data.from);

      void queryClient.invalidateQueries({ queryKey: ['quota'] });
      void queryClient.invalidateQueries({ queryKey: ['mailboxes'] });
      if (data.mailboxId) {
        void queryClient.invalidateQueries({ queryKey: ['mailbox', data.mailboxId] });
      }
    };

    source.addEventListener('message.received', onMessageReceived as EventListener);
    for (const evt of LIFECYCLE_EVENTS) {
      source.addEventListener(evt, onLifecycle as EventListener);
    }

    return () => {
      source.removeEventListener('message.received', onMessageReceived as EventListener);
      for (const evt of LIFECYCLE_EVENTS) {
        source.removeEventListener(evt, onLifecycle as EventListener);
      }
      source.close();
    };
  }, [enabled, activeSubscriptionId, queryClient, t]);
}
